import { dataLayer } from '../../../data/dataLayer'
import {
  AssessmentBlueprint,
  AssessmentAttempt,
  AssessmentReport,
} from '../../../assessment/types'
import { debug } from '../../../utils/logger'

/**
 * Seeds realistic assessment data directly into the dataLayer.
 * This is separate from the MultiAccountStateV4 because assessments
 * are stored in their own domain (D3).
 */
export async function seedDemoAssessments(
  learnerIds: Record<string, string>,
  teacherId?: string,
  classroomId?: string,
): Promise<void> {
  const now = Date.now()
  const dayMs = 24 * 60 * 60 * 1000

  const { djayden, sophie, emma, noah, liam, noor } = learnerIds

  // Helper to create a report
  const createReport = (
    attemptId: string,
    blueprintId: string,
    learnerId: string,
    group: number,
    completedAt: number,
    scorePercent: number,
    totalQuestions: number,
    domainId: string,
    modules: { id: string; title: string; score: number; levelCode: string }[],
    overallLevelCode: string,
    overallTier: number,
  ): AssessmentReport => {
    const correctCount = Math.round((scorePercent / 100) * totalQuestions)

    return {
      attemptId,
      blueprintId,
      completedAt,
      selectedGroup: group,
      score: {
        correctCount,
        totalQuestions,
        percentage: scorePercent,
      },
      time: {
        totalSeconds: totalQuestions * 45,
        avgPerQuestion: 45,
      },
      byModule: modules.map((m) => {
        const mCorrect = Math.round(
          (m.score / 100) * (totalQuestions / modules.length),
        )
        return {
          moduleId: m.id as any,
          totalQuestions: Math.round(totalQuestions / modules.length),
          correctCount: mCorrect,
          accuracy: m.score,
          avgTimePerQuestion: 45,
          // Add these for the UI
          estimatedLevelCode: m.levelCode,
          scorePercentage: m.score,
        } as any
      }),
      byTier: [],
      byFamily: [],
      weakestConcepts: [],
      strongestConcepts: [],
      weakAreas: [],
      strongAreas: [],
      suggestedPractice: [],
      recommendation: 'Goed gedaan! Blijf oefenen.',
      estimatedLevels: modules.map((m) => ({
        moduleId: m.id as any,
        moduleTitle: m.title,
        estimatedTier: overallTier,
        estimatedLevelCode: m.levelCode,
        expectedTier: overallTier,
        expectedLevelCode: overallLevelCode,
        delta: 0,
        deltaLabel: 'op',
        questionCount: Math.round(totalQuestions / modules.length),
        accuracy: m.score,
      })),
      overallEstimatedTier: overallTier,
      overallEstimatedLevelCode: overallLevelCode,
      overallExpectedTier: overallTier,
      overallExpectedLevelCode: overallLevelCode,
      growthIndicators: [],
      strengthsBySkill: [],
      focusAreasBySkill: [],
      actionPlan: {
        beginMet: null,
        gaVerderMet: [],
        uitdaging: null,
      },
    }
  }

  // Helper to create blueprint and attempt
  const createAssessmentData = async (
    learnerId: string,
    domainId: string,
    group: number,
    daysAgo: number,
    scorePercent: number,
    overallLevelCode: string,
    overallTier: number,
    modules: { id: string; title: string; score: number; levelCode: string }[],
  ) => {
    if (!learnerId) return

    const idSuffix = `${learnerId}_${domainId}_${daysAgo}`
    const blueprintId = `demo_bp_${idSuffix}`
    const attemptId = `demo_att_${idSuffix}`
    const completedAt = now - daysAgo * dayMs

    const blueprint: AssessmentBlueprint = {
      id: blueprintId,
      createdAt: completedAt - 30 * 60 * 1000,
      selectedGroup: group,
      config: {
        questionCount: 20,
        moduleFilter: modules.map((m) => m.id as any),
        minModules: 1,
        countsForMastery: true,
        mode: 'general',
        domainId: domainId as any,
      },
      selectionPlan: {
        weakPercentage: 30,
        mediumPercentage: 40,
        strongPercentage: 30,
        actualDistribution: { weak: 6, medium: 8, strong: 6 },
      },
      questions: [], // Simplified for demo
      masterySnapshot: {
        totalFamilies: 20,
        weakFamilies: 2,
        mediumFamilies: 8,
        strongFamilies: 10,
      },
      eligibilityFilter: 'demo',
      pickedQuestionIds: [],
    }

    const attempt: AssessmentAttempt = {
      id: attemptId,
      blueprintId,
      startedAt: completedAt - 15 * 60 * 1000,
      completedAt,
      selectedGroup: group,
      learnerId,
      countsForMastery: true,
      totalTimeSeconds: 900,
      status: 'completed',
      domainId: domainId as any,
      answers: Array(20)
        .fill(null)
        .map((_, i) => ({
          questionId: `q_${i}`,
          userChoice: 0,
          isCorrect: i < (20 * scorePercent) / 100,
          timeSpent: 45,
          familyId: 'demo_family',
          module: modules[i % modules.length].id as any,
        })),
    }

    const report = createReport(
      attemptId,
      blueprintId,
      learnerId,
      group,
      completedAt,
      scorePercent,
      20,
      domainId,
      modules,
      overallLevelCode,
      overallTier,
    )

    try {
      await dataLayer.saveAssessmentBlueprint(blueprintId, blueprint)
      await dataLayer.saveAssessmentAttempt(attemptId, attempt)
      await dataLayer.saveAssessmentReport(attemptId, report)
    } catch (e) {
      debug('Failed to save demo assessment data for', learnerId, e)
    }
  }

  // ─── Seed Data ─────────────────────────────────────────────────────────────

  // Djayden (Group 7)
  await createAssessmentData(djayden, 'rekenen', 7, 14, 87, 'M7+', 12, [
    { id: 'Basisrekenen', title: 'Basisrekenen', score: 90, levelCode: 'M7+' },
    { id: 'Getalbegrip', title: 'Getalbegrip', score: 85, levelCode: 'M7' },
  ])
  await createAssessmentData(djayden, 'taalverzorging', 7, 7, 72, 'M7', 11, [
    {
      id: 'SpellingWW',
      title: 'Werkwoordspelling',
      score: 70,
      levelCode: 'M7',
    },
    { id: 'SpellingRegel', title: 'Regelspelling', score: 75, levelCode: 'M7' },
  ])

  // Sophie (Group 8)
  await createAssessmentData(sophie, 'taalverzorging', 8, 5, 95, 'E8', 16, [
    {
      id: 'SpellingWW',
      title: 'Werkwoordspelling',
      score: 100,
      levelCode: 'E8+',
    },
    { id: 'SpellingRegel', title: 'Regelspelling', score: 90, levelCode: 'E8' },
  ])

  // Emma (Group 7)
  await createAssessmentData(emma, 'rekenen', 7, 3, 65, 'M7-', 10, [
    { id: 'Basisrekenen', title: 'Basisrekenen', score: 70, levelCode: 'M7' },
    { id: 'Getalbegrip', title: 'Getalbegrip', score: 60, levelCode: 'B7' },
  ])

  // Noah (Group 7)
  await createAssessmentData(noah, 'rekenen', 7, 10, 78, 'M7', 11, [
    { id: 'Basisrekenen', title: 'Basisrekenen', score: 80, levelCode: 'M7' },
    { id: 'Getalbegrip', title: 'Getalbegrip', score: 75, levelCode: 'M7' },
  ])

  // Liam (Group 8)
  await createAssessmentData(liam, 'rekenen', 8, 7, 58, 'B8+', 13, [
    { id: 'Basisrekenen', title: 'Basisrekenen', score: 60, levelCode: 'B8+' },
    { id: 'Getalbegrip', title: 'Getalbegrip', score: 55, levelCode: 'B8' },
  ])

  // Noor (Group 8)
  await createAssessmentData(noor, 'begrijpend-lezen', 8, 4, 82, 'M8+', 15, [
    { id: 'Taalbegrip', title: 'Taalbegrip', score: 82, levelCode: 'M8+' },
  ])

  // ─── Teacher Assignment ("Toets Klaargezet") ──────────────────────────────
  if (teacherId) {
    const assignmentId = `demo_assignment_rek_g7`
    const assignment = {
      id: assignmentId,
      teacherId,
      classroomId: classroomId || 'demo_classroom',
      domainId: 'rekenen',
      domainLabel: 'Rekenen',
      group: 7,
      questionCount: 15,
      estimatedMinutes: 20,
      targetStudentIds: [],
      wholeClass: true,
      selectedFamilyIds: [
        'REK-CIJF-PLUS',
        'REK-CIJF-MIN',
        'REK-CIJF-KEER',
        'GET-BREUK-BASIS',
        'GET-BREUK-LEZEN',
      ],
      type: 'targeted' as const,
      selectedSkillSummary: 'cijferend rekenen, breuken basis',
      createdAt: now - 2 * 24 * 60 * 60 * 1000, // 2 days ago
      deadline: now + 5 * 24 * 60 * 60 * 1000, // 5 days from now
      status: 'active',
      completedByStudentIds: [],
    }
    try {
      await dataLayer.saveTeacherAssignment(assignmentId, assignment)
      debug('✅ Demo teacher assignment (toets klaargezet) seeded')
    } catch (e) {
      debug('Failed to save teacher assignment', e)
    }
  }

  debug('✅ Demo assessment data seeded successfully')
}
